/**
 * Compteur d'Animation Avancé pour Shopify
 * 
 * Ce script permet d'animer des nombres avec un effet de compteur
 * Il supporte les pourcentages, valeurs décimales et formats numériques
 */
class CounterAnimation {
  constructor(options = {}) {
    this.defaults = {
      selector: '.count-animation',
      speed: 2000,
      delay: 0,
      easing: 'easeOutExpo',
      decimals: 0,
      separator: '',
      onComplete: null,
      startOnView: true
    };

    this.settings = { ...this.defaults, ...options };
    this.elements = document.querySelectorAll(this.settings.selector);
    this.easings = {
      linear: t => t,
      easeInQuad: t => t * t,
      easeOutQuad: t => t * (2 - t),
      easeInOutQuad: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
      easeInCubic: t => t * t * t,
      easeOutCubic: t => (--t) * t * t + 1,
      easeInOutCubic: t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
      easeInQuart: t => t * t * t * t,
      easeOutQuart: t => 1 - (--t) * t * t * t,
      easeInOutQuart: t => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t,
      easeOutExpo: t => t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
    };
    
    this.initialized = false;
    this.init();
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    
    if (this.settings.startOnView) {
      this.setupScrollObserver();
    } else {
      this.animateAll();
    }
  }

  setupScrollObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('counted')) {
          entry.target.classList.add('counted');
          // Démarre l'animation uniquement lorsque l'élément est visible
          this.animateElement(entry.target);
        }
      });
    }, options);

    this.elements.forEach(element => {
      observer.observe(element);
    });
  }

  animateAll() {
    this.elements.forEach(element => {
      if (!element.classList.contains('counted')) {
        element.classList.add('counted');
        this.animateElement(element);
      }
    });
  }

  animateElement(element) {
    // Obtenir les valeurs pour cette animation
    const finalValue = parseFloat(element.getAttribute('data-value') || 0);
    const decimals = parseInt(element.getAttribute('data-decimals') || this.settings.decimals);
    const duration = parseInt(element.getAttribute('data-duration') || this.settings.speed);
    const delay = parseInt(element.getAttribute('data-delay') || this.settings.delay);
    const easing = element.getAttribute('data-easing') || this.settings.easing;
    const separator = element.getAttribute('data-separator') || this.settings.separator;
    
    // Vérifier si on a un symbole (%, €, etc.)
    const hasSymbol = element.querySelector('.symbol') !== null;
    const symbol = hasSymbol ? element.querySelector('.symbol').textContent : '';
    
    // Délai avant de commencer l'animation
    setTimeout(() => {
      this.countUp(element, finalValue, duration, decimals, easing, separator, symbol);
    }, delay);
  }

  countUp(element, finalValue, duration, decimals, easingName, separator, symbol) {
    const startValue = 0;
    const startTime = performance.now();
    const easingFunction = this.easings[easingName] || this.easings.easeOutExpo;
    const countDisplay = element.querySelector('.count') || element;

    const updateCount = (timestamp) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easingFunction(progress);
      
      // Calculer la valeur actuelle
      const currentValue = startValue + (finalValue - startValue) * easedProgress;
      
      // Formater la valeur
      const formattedValue = this.formatNumber(currentValue, decimals, separator);
      
      // Mettre à jour l'affichage
      countDisplay.textContent = formattedValue;
      
      // Continuer l'animation si pas terminée
      if (progress < 1) {
        requestAnimationFrame(updateCount);
      } else {
        // Animation terminée
        countDisplay.textContent = this.formatNumber(finalValue, decimals, separator);
        
        // Appeler la callback de fin si définie
        if (typeof this.settings.onComplete === 'function') {
          this.settings.onComplete(element);
        }
      }
    };
    
    // Démarrer l'animation
    requestAnimationFrame(updateCount);
  }

  formatNumber(value, decimals, separator) {
    const fixed = value.toFixed(decimals);
    if (separator && separator.length > 0) {
      const parts = fixed.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      return parts.join('.');
    }
    return fixed;
  }
}

// Exporter pour Shopify
if (typeof window !== 'undefined') {
  window.CounterAnimation = CounterAnimation;
}